import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  Users, 
  Bell, 
  Star, 
  ChevronRight,
  Plus,
  Filter,
  Search,
  Globe,
  Award,
  TrendingUp,
  BookOpen,
  Camera,
  Video,
  FileText,
  Link,
  MoreHorizontal,
  CheckCircle,
  AlertCircle,
  Info,
  Zap,
  Target,
  Home,
  GraduationCap,
  Briefcase,
  CreditCard,
  Shield,
  Heart,
  MessageSquare,
  ExternalLink,
  Download,
  Share2,
  ThumbsUp,
  Eye,
  CalendarDays,
  Timer,
  Flag,
  User,
  Building,
  Phone,
  Mail,
  Webcam,
  Mic,
  MicOff,
  VideoOff,
  Settings,
  Edit,
  Trash2,
  Copy,
  Archive,
  Pin,
  Unpin
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';
import { AnimatedCounter } from '../components/ui';

interface Event {
  id: string;
  title: string;
  description: string;
  type: 'workshop' | 'webinar' | 'deadline' | 'reminder' | 'milestone' | 'meeting' | 'class' | 'event';
  category: string;
  startDate: string;
  endDate?: string;
  time: string;
  timezone: string;
  location?: string;
  isOnline: boolean;
  meetingLink?: string;
  organizer: {
    name: string;
    avatar: string;
    verified: boolean;
  };
  capacity?: number;
  registered: number;
  price: number; // 0 for free
  currency: string;
  tags: string[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  language: string;
  isRecurring: boolean;
  recurrence?: string;
  isBookmarked: boolean;
  isRegistered: boolean;
  reminderSet: boolean;
  reminderTime?: string;
  status: 'upcoming' | 'live' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  attachments?: {
    name: string;
    url: string;
    type: string;
  }[];
  requirements?: string[];
  learningOutcomes?: string[];
  speaker?: {
    name: string;
    title: string;
    company: string;
    bio: string;
    avatar: string;
  };
}

interface TimelineItem {
  id: string;
  title: string;
  description: string;
  type: 'milestone' | 'deadline' | 'reminder' | 'achievement' | 'goal';
  date: string;
  time: string;
  category: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'pending' | 'completed' | 'overdue' | 'cancelled';
  isCompleted: boolean;
  completedAt?: string;
  tags: string[];
  relatedEventId?: string;
  relatedMilestoneId?: string;
  actionRequired: boolean;
  actionUrl?: string;
  actionText?: string;
}

interface UpcomingStats {
  totalEvents: number;
  upcomingThisWeek: number;
  deadlinesThisMonth: number;
  completedThisMonth: number;
  bookmarkedEvents: number;
  registeredEvents: number;
  remindersSet: number;
  overdueItems: number;
}

const UpcomingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [events, setEvents] = useState<Event[]>([]);
  const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
  const [stats, setStats] = useState<UpcomingStats | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedView, setSelectedView] = useState<'timeline' | 'events' | 'calendar'>('timeline');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [showFilters, setShowFilters] = useState(false);

  useSEO({
    title: 'Upcoming - MyNewStart',
    description: 'Timeline view of events, workshops, and important reminders for your American journey.',
    keywords: ['upcoming', 'events', 'timeline', 'workshops', 'deadlines', 'reminders'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Dashboard', url: '/dashboard' },
      { name: 'Upcoming', url: '/upcoming' }
    ]
  });

  useEffect(() => {
    loadUpcomingData();
  }, []);

  const loadUpcomingData = () => {
    // Mock data - in a real app, this would come from your API
    const mockEvents: Event[] = [
      {
        id: '1',
        title: 'Building Credit Workshop',
        description: 'Learn how to establish and build your credit history in the US. We\'ll cover credit scores, credit cards, and strategies for improvement.',
        type: 'workshop',
        category: 'banking',
        startDate: '2024-01-25',
        endDate: '2024-01-25',
        time: '14:00',
        timezone: 'EST',
        location: 'Online',
        isOnline: true,
        meetingLink: 'https://zoom.us/j/123456789',
        organizer: {
          name: 'Sarah Johnson',
          avatar: '/api/placeholder/40/40',
          verified: true
        },
        capacity: 50,
        registered: 23,
        price: 0,
        currency: 'USD',
        tags: ['credit', 'banking', 'financial literacy'],
        difficulty: 'beginner',
        language: 'English',
        isRecurring: false,
        isBookmarked: true,
        isRegistered: false,
        reminderSet: true,
        reminderTime: '1 hour before',
        status: 'upcoming',
        priority: 'high',
        learningOutcomes: [
          'Understand credit scores and reports',
          'Learn about different types of credit',
          'Develop a credit building strategy'
        ],
        speaker: {
          name: 'Sarah Johnson',
          title: 'Financial Advisor',
          company: 'Chase Bank',
          bio: 'Certified financial advisor with 8 years of experience helping immigrants build credit.',
          avatar: '/api/placeholder/60/60'
        }
      },
      {
        id: '2',
        title: 'Tax Filing Deadline Reminder',
        description: 'Don\'t forget! Tax filing deadline is approaching. Make sure you have all your documents ready.',
        type: 'deadline',
        category: 'taxes',
        startDate: '2024-04-15',
        time: '23:59',
        timezone: 'EST',
        location: 'N/A',
        isOnline: false,
        organizer: {
          name: 'MyNewStart Team',
          avatar: '/api/placeholder/40/40',
          verified: true
        },
        price: 0,
        currency: 'USD',
        tags: ['taxes', 'deadline', 'important'],
        difficulty: 'beginner',
        language: 'English',
        isRecurring: true,
        isBookmarked: false,
        isRegistered: false,
        reminderSet: true,
        reminderTime: '1 week before',
        status: 'upcoming',
        priority: 'urgent'
      },
      {
        id: '3',
        title: 'Housing Search Webinar',
        description: 'Everything you need to know about finding and securing housing in the US. From rental applications to understanding leases.',
        type: 'webinar',
        category: 'housing',
        startDate: '2024-01-28',
        endDate: '2024-01-28',
        time: '19:00',
        timezone: 'EST',
        location: 'Online',
        isOnline: true,
        meetingLink: 'https://zoom.us/j/987654321',
        organizer: {
          name: 'Maria Rodriguez',
          avatar: '/api/placeholder/40/40',
          verified: true
        },
        capacity: 100,
        registered: 67,
        price: 0,
        currency: 'USD',
        tags: ['housing', 'rental', 'real estate'],
        difficulty: 'beginner',
        language: 'English',
        isRecurring: false,
        isBookmarked: false,
        isRegistered: true,
        reminderSet: true,
        reminderTime: '30 minutes before',
        status: 'upcoming',
        priority: 'medium',
        learningOutcomes: [
          'Understand rental market basics',
          'Learn about rental applications',
          'Know your rights as a tenant'
        ]
      },
      {
        id: '4',
        title: 'Career Networking Event',
        description: 'Connect with professionals in your field and learn about job opportunities. Bring your resume and business cards!',
        type: 'event',
        category: 'career',
        startDate: '2024-02-03',
        endDate: '2024-02-03',
        time: '18:00',
        timezone: 'EST',
        location: 'Convention Center, Downtown',
        isOnline: false,
        organizer: {
          name: 'Tech Professionals Network',
          avatar: '/api/placeholder/40/40',
          verified: true
        },
        capacity: 200,
        registered: 145,
        price: 25,
        currency: 'USD',
        tags: ['networking', 'career', 'jobs'],
        difficulty: 'intermediate',
        language: 'English',
        isRecurring: false,
        isBookmarked: true,
        isRegistered: false,
        reminderSet: false,
        status: 'upcoming',
        priority: 'medium'
      }
    ];

    const mockTimelineItems: TimelineItem[] = [
      {
        id: '1',
        title: 'Complete Profile Setup',
        description: 'Finish setting up your profile to unlock personalized content and recommendations.',
        type: 'milestone',
        date: '2024-01-22',
        time: '09:00',
        category: 'profile',
        priority: 'high',
        status: 'pending',
        isCompleted: false,
        tags: ['profile', 'setup', 'onboarding'],
        actionRequired: true,
        actionUrl: '/profile-setup',
        actionText: 'Complete Setup'
      },
      {
        id: '2',
        title: 'Apply for SSN',
        description: 'Submit your Social Security Number application at the local SSA office.',
        type: 'deadline',
        date: '2024-01-24',
        time: '10:00',
        category: 'documents',
        priority: 'urgent',
        status: 'pending',
        isCompleted: false,
        tags: ['ssn', 'documents', 'government'],
        actionRequired: true,
        actionUrl: '/guides/ssn-application',
        actionText: 'View Guide'
      },
      {
        id: '3',
        title: 'First Credit Card Application',
        description: 'Apply for your first secured credit card to start building credit history.',
        type: 'milestone',
        date: '2024-01-26',
        time: '14:00',
        category: 'banking',
        priority: 'high',
        status: 'pending',
        isCompleted: false,
        tags: ['credit', 'banking', 'financial'],
        actionRequired: true,
        actionUrl: '/guides/credit-cards',
        actionText: 'Learn More'
      },
      {
        id: '4',
        title: 'Profile Setup Completed',
        description: 'Successfully completed your profile setup and unlocked personalized features.',
        type: 'achievement',
        date: '2024-01-20',
        time: '16:30',
        category: 'profile',
        priority: 'medium',
        status: 'completed',
        isCompleted: true,
        completedAt: '2024-01-20T16:30:00Z',
        tags: ['profile', 'achievement', 'milestone'],
        actionRequired: false
      }
    ];

    const mockStats: UpcomingStats = {
      totalEvents: 12,
      upcomingThisWeek: 3,
      deadlinesThisMonth: 5,
      completedThisMonth: 8,
      bookmarkedEvents: 4,
      registeredEvents: 2,
      remindersSet: 7,
      overdueItems: 1
    };

    setEvents(mockEvents);
    setTimelineItems(mockTimelineItems);
    setStats(mockStats);
  };

  const categories = [
    { id: 'all', name: 'All Categories', icon: Globe, color: 'bg-gray-500' },
    { id: 'banking', name: 'Banking & Credit', icon: CreditCard, color: 'bg-blue-500' },
    { id: 'housing', name: 'Housing & Living', icon: Home, color: 'bg-green-500' },
    { id: 'career', name: 'Career & Education', icon: GraduationCap, color: 'bg-purple-500' },
    { id: 'taxes', name: 'Taxes & Finance', icon: FileText, color: 'bg-orange-500' },
    { id: 'documents', name: 'Documents & Forms', icon: FileText, color: 'bg-red-500' },
    { id: 'profile', name: 'Profile & Setup', icon: User, color: 'bg-teal-500' }
  ];

  const eventTypes = [
    { id: 'all', name: 'All Types', icon: Calendar },
    { id: 'workshop', name: 'Workshops', icon: BookOpen },
    { id: 'webinar', name: 'Webinars', icon: Video },
    { id: 'deadline', name: 'Deadlines', icon: AlertCircle },
    { id: 'reminder', name: 'Reminders', icon: Bell },
    { id: 'milestone', name: 'Milestones', icon: Target },
    { id: 'meeting', name: 'Meetings', icon: Users },
    { id: 'class', name: 'Classes', icon: GraduationCap },
    { id: 'event', name: 'Events', icon: Calendar }
  ];

  const filteredEvents = events.filter(event => {
    const matchesCategory = selectedCategory === 'all' || event.category === selectedCategory;
    const matchesType = selectedType === 'all' || event.type === selectedType;
    const matchesSearch = searchTerm === '' || 
      event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      event.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesType && matchesSearch;
  });

  const filteredTimelineItems = timelineItems.filter(item => {
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    const matchesType = selectedType === 'all' || item.type === selectedType;
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesType && matchesSearch;
  });

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'workshop': return 'text-blue-600 bg-blue-100';
      case 'webinar': return 'text-purple-600 bg-purple-100';
      case 'deadline': return 'text-red-600 bg-red-100';
      case 'reminder': return 'text-yellow-600 bg-yellow-100';
      case 'milestone': return 'text-green-600 bg-green-100';
      case 'meeting': return 'text-indigo-600 bg-indigo-100';
      case 'class': return 'text-orange-600 bg-orange-100';
      case 'event': return 'text-pink-600 bg-pink-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'text-red-600 bg-red-100';
      case 'high': return 'text-orange-600 bg-orange-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'low': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-blue-600 bg-blue-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      case 'cancelled': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  if (!stats) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          <Calendar className="w-10 h-10 inline-block mr-3 text-primary-600" />
          Upcoming
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Timeline view of events, workshops, and important reminders for your American journey.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Calendar className="w-6 h-6 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={stats.totalEvents} />
          </div>
          <p className="text-sm text-gray-600">Total Events</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <Clock className="w-6 h-6 text-green-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={stats.upcomingThisWeek} />
          </div>
          <p className="text-sm text-gray-600">This Week</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <AlertCircle className="w-6 h-6 text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={stats.deadlinesThisMonth} />
          </div>
          <p className="text-sm text-gray-600">Deadlines This Month</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 text-center">
          <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-3">
            <CheckCircle className="w-6 h-6 text-purple-600" />
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-1">
            <AnimatedCounter value={stats.completedThisMonth} />
          </div>
          <p className="text-sm text-gray-600">Completed This Month</p>
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setSelectedView('timeline')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedView === 'timeline' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Timeline
          </button>
          <button
            onClick={() => setSelectedView('events')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedView === 'events' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Events
          </button>
          <button
            onClick={() => setSelectedView('calendar')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              selectedView === 'calendar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Calendar
          </button>
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className="btn btn-secondary flex items-center"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filters
        </button>
      </div>

      {/* Search and Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search events and timeline items..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-3">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Categories</option>
                <option value="banking">Banking & Credit</option>
                <option value="housing">Housing & Living</option>
                <option value="career">Career & Education</option>
                <option value="taxes">Taxes & Finance</option>
                <option value="documents">Documents & Forms</option>
                <option value="profile">Profile & Setup</option>
              </select>

              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="all">All Types</option>
                <option value="workshop">Workshops</option>
                <option value="webinar">Webinars</option>
                <option value="deadline">Deadlines</option>
                <option value="reminder">Reminders</option>
                <option value="milestone">Milestones</option>
                <option value="meeting">Meetings</option>
                <option value="class">Classes</option>
                <option value="event">Events</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center px-4 py-2 rounded-full border transition-all duration-200 ${
              selectedCategory === category.id
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-600 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            <category.icon className="w-4 h-4 mr-2" />
            {category.name}
          </button>
        ))}
      </div>

      {/* Content */}
      {selectedView === 'timeline' ? (
        <div className="space-y-6">
          {filteredTimelineItems.map((item, index) => (
            <div
              key={item.id}
              className={`bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden ${
                item.isCompleted ? 'opacity-75' : ''
              }`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center mr-4 ${
                      item.isCompleted ? 'bg-green-100' : 
                      item.priority === 'urgent' ? 'bg-red-100' :
                      item.priority === 'high' ? 'bg-orange-100' :
                      'bg-blue-100'
                    }`}>
                      {item.isCompleted ? (
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      ) : item.type === 'milestone' ? (
                        <Target className="w-6 h-6 text-blue-600" />
                      ) : item.type === 'deadline' ? (
                        <AlertCircle className="w-6 h-6 text-red-600" />
                      ) : item.type === 'reminder' ? (
                        <Bell className="w-6 h-6 text-yellow-600" />
                      ) : (
                        <Award className="w-6 h-6 text-purple-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{item.title}</h3>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(item.type)}`}>
                          {item.type}
                        </span>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(item.priority)}`}>
                          {item.priority}
                        </span>
                      </div>
                      <p className="text-gray-600 mb-3">{item.description}</p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          {formatDate(item.date)}
                        </div>
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          {formatTime(item.time)}
                        </div>
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                          {item.status}
                        </span>
                      </div>
                      {item.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-4">
                          {item.tags.map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                            >
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                      {item.actionRequired && !item.isCompleted && (
                        <button
                          onClick={() => item.actionUrl && navigate(item.actionUrl)}
                          className="btn btn-primary"
                        >
                          {item.actionText || 'Take Action'}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="p-2 text-gray-400 hover:text-gray-600">
                      <MoreHorizontal className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : selectedView === 'events' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredEvents.map((event) => (
            <div
              key={event.id}
              className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
            >
              {/* Event Header */}
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center">
                    <img
                      src={event.organizer.avatar}
                      alt={event.organizer.name}
                      className="w-10 h-10 rounded-full mr-3"
                    />
                    <div>
                      <div className="flex items-center">
                        <h3 className="font-semibold text-gray-900">{event.organizer.name}</h3>
                        {event.organizer.verified && (
                          <CheckCircle className="w-4 h-4 text-blue-500 ml-2" />
                        )}
                      </div>
                      <p className="text-sm text-gray-500">Organizer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(event.type)}`}>
                      {event.type}
                    </span>
                    <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(event.priority)}`}>
                      {event.priority}
                    </span>
                  </div>
                </div>

                <h2 className="text-xl font-bold text-gray-900 mb-3">{event.title}</h2>
                <p className="text-gray-600 mb-4 line-clamp-3">{event.description}</p>

                {/* Event Details */}
                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="w-4 h-4 mr-2" />
                    {formatDate(event.startDate)} at {formatTime(event.time)} {event.timezone}
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    {event.isOnline ? (
                      <>
                        <Video className="w-4 h-4 mr-2" />
                        Online Event
                      </>
                    ) : (
                      <>
                        <MapPin className="w-4 h-4 mr-2" />
                        {event.location}
                      </>
                    )}
                  </div>
                  {event.capacity && (
                    <div className="flex items-center text-sm text-gray-500">
                      <Users className="w-4 h-4 mr-2" />
                      {event.registered}/{event.capacity} registered
                    </div>
                  )}
                </div>

                {/* Tags */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {event.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-block px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  {event.isRegistered ? (
                    <button className="flex-1 bg-green-100 text-green-700 py-2 px-4 rounded-lg font-medium">
                      Registered
                    </button>
                  ) : (
                    <button className="flex-1 bg-primary-600 text-white py-2 px-4 rounded-lg hover:bg-primary-700 transition-colors duration-200">
                      Register
                    </button>
                  )}
                  <button className={`p-2 rounded-lg transition-colors ${
                    event.isBookmarked ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                    <Star className={`w-4 h-4 ${event.isBookmarked ? 'fill-current' : ''}`} />
                  </button>
                  <button className={`p-2 rounded-lg transition-colors ${
                    event.reminderSet ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}>
                    <Bell className={`w-4 h-4 ${event.reminderSet ? 'fill-current' : ''}`} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-8 text-center">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Calendar View</h3>
          <p className="text-gray-600 mb-6">
            Calendar view is coming soon! For now, use the Timeline or Events view to see your upcoming items.
          </p>
          <button
            onClick={() => setSelectedView('timeline')}
            className="btn btn-primary"
          >
            View Timeline
          </button>
        </div>
      )}

      {/* Empty State */}
      {(selectedView === 'timeline' && filteredTimelineItems.length === 0) || 
       (selectedView === 'events' && filteredEvents.length === 0) ? (
        <div className="text-center py-12">
          <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">No items found</h3>
          <p className="text-gray-600 mb-6">
            Try adjusting your search criteria or explore different categories.
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setSelectedCategory('all');
              setSelectedType('all');
            }}
            className="btn btn-primary"
          >
            Clear Filters
          </button>
        </div>
      ) : null}

      {/* Call to Action */}
      <div className="mt-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-4">Stay on Track with Your Journey</h2>
        <p className="text-lg mb-6 opacity-90">
          Never miss important deadlines or events. Set reminders, bookmark events, and track your progress.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => navigate('/next-steps')}
            className="btn bg-white text-primary-600 hover:bg-gray-100"
          >
            View Next Steps
          </button>
          <button
            onClick={() => navigate('/milestones')}
            className="btn border-2 border-white text-white hover:bg-white hover:text-primary-600"
          >
            Track Milestones
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpcomingPage;
